import { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { API, getUserPhone } from '../../lib/api';

const QUESTIONS = [
  'I feel emotionally drained after my workday',
  'I feel tired when I wake up and have to face another day at work',
  'I feel less connected to my patients than I used to',
  'Small frustrations at work irritate me more than they should',
  'I doubt the significance of my work',
  'I have trouble switching off from work at home',
  'I postpone my own meals, sleep or health because of work',
  'I feel I have little control over my workload',
];
const OPTIONS = ['Never', 'Sometimes', 'Often', 'Always'];

const levelFor = (score) => (score <= 8 ? 'Low' : score <= 16 ? 'Moderate' : 'High');
const LEVEL_INFO = {
  Low: { color: '#34d399', text: 'You seem to be coping well right now. Keep protecting your sleep, breaks and boundaries — prevention is easier than recovery.' },
  Moderate: { color: '#fbbf24', text: 'You are carrying noticeable strain. Small daily recovery habits — micro-breaks, a hard stop time, one protected hour — can make a real difference. Consider talking it through.' },
  High: { color: '#f87171', text: 'You are carrying a heavy load, and it deserves attention. Please consider speaking with someone you trust — a colleague, mentor or mental-health professional. You matter beyond your work.' },
};

export default function BurnoutCheck({ onHome, onTalk }) {
  const [answers, setAnswers] = useState(Array(QUESTIONS.length).fill(null));
  const [result, setResult] = useState(null);
  const answered = answers.filter((a) => a !== null).length;

  const submit = async () => {
    const score = answers.reduce((s, a) => s + (a || 0), 0);
    const level = levelFor(score);
    setResult({ score, level });
    try {
      await axios.post(`${API}/api/gia/burnout`, { phone: getUserPhone() || 'anon', score, level });
    } catch { /* soft fail */ }
    toast.success('Self-check complete');
  };

  return (
    <main className="relative z-10 max-w-2xl mx-auto px-5 pb-20 pt-8" data-testid="burnout-check">
      <button onClick={onHome} data-testid="burnout-back-btn" className="text-xs font-bold text-purple-300 mb-4">← Gia Home</button>
      <h1 className="text-3xl font-black text-white tracking-tight mb-1">Burnout Self-Check</h1>
      <p className="text-white/60 text-sm mb-6">8 questions · 2 minutes · private. A self-reflection tool, <strong className="text-white/80">not a diagnosis</strong>.</p>

      {!result ? (
        <>
          <div className="space-y-5">
            {QUESTIONS.map((q, qi) => (
              <div key={qi} className="rounded-2xl p-4 border border-white/10" style={{ background: 'rgba(255,255,255,0.04)' }}>
                <p className="text-sm text-white/90 font-medium mb-3">{qi + 1}. {q}</p>
                <div className="grid grid-cols-4 gap-1.5">
                  {OPTIONS.map((o, oi) => (
                    <button
                      key={o}
                      onClick={() => setAnswers((a) => a.map((v, i) => (i === qi ? oi : v)))}
                      data-testid={`burnout-q${qi}-${oi}`}
                      className={`py-2 rounded-xl text-[11px] font-bold transition-colors border ${
                        answers[qi] === oi
                          ? 'bg-purple-500/40 border-purple-300/60 text-white'
                          : 'bg-white/5 border-white/10 text-white/60 hover:bg-white/10'
                      }`}
                    >
                      {o}
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </div>
          <button
            onClick={submit}
            disabled={answered < QUESTIONS.length}
            data-testid="burnout-submit-btn"
            className="w-full mt-6 py-4 rounded-full text-sm font-black text-white shadow-xl disabled:opacity-40 transition-transform active:scale-[0.98]"
            style={{ background: 'linear-gradient(135deg,#a855f7,#7c3aed)' }}
          >
            {answered < QUESTIONS.length ? `Answer all questions (${answered}/${QUESTIONS.length})` : 'SEE MY RESULT'}
          </button>
        </>
      ) : (
        <div className="rounded-3xl p-6 border border-white/10 text-center" style={{ background: 'rgba(255,255,255,0.05)' }} data-testid="burnout-result">
          <p className="text-[10px] font-black tracking-[0.25em] uppercase text-white/50 mb-2">Your strain level</p>
          <p className="text-4xl font-black mb-1" style={{ color: LEVEL_INFO[result.level].color }}>{result.level}</p>
          <p className="text-xs text-white/50 mb-4">score {result.score} / 24</p>
          <div className="h-2 rounded-full bg-white/10 overflow-hidden mb-5">
            <div className="h-full rounded-full transition-all" style={{ width: `${(result.score / 24) * 100}%`, background: LEVEL_INFO[result.level].color }} />
          </div>
          <p className="text-sm text-white/80 leading-relaxed mb-6">{LEVEL_INFO[result.level].text}</p>
          <button
            onClick={() => onTalk(`I just did a burnout self-check and my strain level came out as ${result.level} (${result.score}/24). Can you help me process this and plan small steps?`)}
            data-testid="burnout-talk-gia-btn"
            className="w-full py-3.5 rounded-full text-sm font-black text-white shadow-lg mb-3 transition-transform active:scale-[0.98]"
            style={{ background: 'linear-gradient(135deg,#a855f7,#7c3aed)' }}
          >
            💜 Talk it through with Gia
          </button>
          <button onClick={() => { setResult(null); setAnswers(Array(QUESTIONS.length).fill(null)); }} data-testid="burnout-retake-btn" className="text-xs font-bold text-white/50">Retake</button>
          <p className="text-[10px] text-white/40 mt-4">If you are in crisis or having thoughts of self-harm, please reach out immediately — iCall: 9152987821 (India).</p>
        </div>
      )}
    </main>
  );
}
